import { watch } from 'vue';
import { DesignerFontLoader } from './DesignerFontLoader.js';

/**
 *
 * Generic font binding used by all Designer widgets.
 *
 * Responsibilities:
 * - resolves FontResource
 * - loads fonts through DesignerFontLoader
 * - manages watcher lifecycle
 * - avoids duplicate loads
 * - exposes fallback and loaded font transitions
 *
 * Widgets are responsible only for:
 * - selecting the FontResource
 * - selecting the fallback family
 * - applying the resolved family to their rendering objects
 
 * FontBinding — encapsulates the load/watch/fallback dance around a single
 * Font Resource so widgets (HaspLabel, HaspCheckbox, HaspButtonmatrix,
 * HaspGauge, ...) don't each re-implement it against DesignerFontLoader
 * directly. Deliberately has no Konva or widget-class dependency: it only
 * ever calls back as onChange(family, loaded) — family is the effective font
 * family to use right now, loaded is false while it's still the fallback and
 * true once/if it's the asynchronously resolved FontResource. Widgets that
 * don't care about the distinction can simply ignore the second argument.
 *
 * One instance per font "slot" a widget needs (text_font, value_font,
 * scale_font, ...), kept for the widget's whole lifetime. Widgets are meant
 * to call set(resource, fallbackFamily) unconditionally on every style
 * recompute — set() does its own change detection (by resource uuid and
 * fallback identity) so a no-op recompute is a cheap early return: no watch
 * restart, no re-load, no redundant callback.
 */
export class FontBinding {
  constructor(onChange) {
    this._onChange = onChange;
    this._uuid = null;
    this._fallbackFamily = undefined;
    this._stopWatch = null;
    this._isLoaded = false; // whether the current uuid's font is the active (non-fallback) family
  }

  /**
   * Point this binding at a FontResource (or null) with the fallback family
   * to use until/unless it's loaded.
   */
  set(resource, fallbackFamily) {
    const uuid = resource?.uuid ?? null;
    const uuidChanged = uuid !== this._uuid;
    const fallbackChanged = fallbackFamily !== this._fallbackFamily;

    if (!uuidChanged && !fallbackChanged) return;

    this._fallbackFamily = fallbackFamily;

    if (!uuidChanged) {
      if (!this._isLoaded) this._onChange?.(this._fallbackFamily, false);
      return;
    }

    this.dispose();
    this._uuid = uuid;
    this._isLoaded = false;

    this._onChange?.(this._fallbackFamily, false); // until loaded, or if there's no resource / it never loads
    if (!uuid) return;

    DesignerFontLoader.loadFont(uuid);
    this._stopWatch = watch(
      () => DesignerFontLoader.getStatus(uuid),
      (status) => {
        if (status !== 'loaded') return;
        this._isLoaded = true;
        this._onChange?.(DesignerFontLoader.getFontFamily(uuid), true);
      },
      { immediate: true },
    );
  }

  /** Stop watching. Call from the owning widget's destroy/cleanup path. */
  dispose() {
    this._stopWatch?.();
    this._stopWatch = null;
  }
}
